import React, { useState, useEffect, useRef } from "react";
import ClickOnMap from "../components/clickOnMap/ClickOnMap";
import getHumanAddress from "../utils/getHumanAddress";
import { AutoComplete, Button, Input, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { autosuggestAddress, geocodeAddress } from "./hereGeocoding";
import { validCoordinates } from "./locationValidation";
import { uniqueDirectoryRecords } from "./directoryOptions";
import { normalizeHereLanguage } from "./hereMaps";
import { applyCheckpointMapPoint, applyCheckpointAddressResult } from "./independentLocationFields";
import "./CheckpointMapEditor.css";

const directoryLocationAddress = (location = {}) =>
  location.address?.formatted ||
  location.address?.streetName ||
  location.address?.cityName ||
  location.name ||
  "";

const directoryLocationSearchText = (location = {}) =>
  [
    location.name,
    ...(Array.isArray(location.aliases) ? location.aliases : []),
    ...(Array.isArray(location.codes) ? location.codes.map((code) => code?.value) : []),
    location.address?.formatted,
    location.address?.streetName,
    location.address?.cityName,
    location.address?.countryCode,
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();

const CheckpointMapEditor = ({
  orderDetail,
  onChange,
  shipmentType,
  plannedRoute = [],
  directoryLocations = [],
  onDirectoryLocationSelect,
  onCreateDirectoryLocation,
  showErrors = false,
}) => {
  const [points, setPoints] = useState([]);
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);
  const [touched, setTouched] = useState({});
  const [addressOptionsByIndex, setAddressOptionsByIndex] = useState({});
  const suggestTimeoutRef = useRef(null);
  const suggestSequenceRef = useRef(0);
  const resolveSequenceRef = useRef(0);
  const addressEnrichmentSequenceRef = useRef(0);
  const { hereApiKey } = useGlobalSettings();
  const mapLanguage = normalizeHereLanguage(i18n?.resolvedLanguage || i18n?.language || "en");

  const minMarkers = shipmentType?.startsWith("CONTAINER") ? 3 : 2;

  const emitChange = (nextPoints) => {
    if (!onChange) return;
    onChange(nextPoints);
  };

  useEffect(() => {
    return () => {
      if (suggestTimeoutRef.current) {
        window.clearTimeout(suggestTimeoutRef.current);
        suggestTimeoutRef.current = null;
      }
    };
  }, []);

  const enrichWithAddresses = async (list) => {
    const results = [];
    for (const p of list) {
      if (p.placeId || p.lat == null || p.lng == null || p.address) {
        results.push({
          ...p,
          address: p.address || "",
          name: p.name || "",
          countryCode: p.countryCode || "",
        });
        continue;
      }
      const address = await getHumanAddress(p.lat, p.lng, hereApiKey, mapLanguage);
      results.push({ ...p, address, name: p.name || address, countryCode: p.countryCode || "" });
    }
    return results;
  };

  useEffect(() => {
    if (!orderDetail?.checkPoints) return;

    const parsed = orderDetail.checkPoints.map((p) => ({
      ...p,
      lat: typeof p.latitude === "number" ? p.latitude : null,
      lng: typeof p.longitude === "number" ? p.longitude : null,
      address: p.address || "",
      name: p.name || "",
      countryCode: p.countryCode || "",
      placeId: p.placeId || "",
    }));

    const base = [...parsed];
    while (base.length < minMarkers) {
      base.push({ lat: null, lng: null, address: "", name: "" });
    }

    const sequenceId = addressEnrichmentSequenceRef.current + 1;
    addressEnrichmentSequenceRef.current = sequenceId;
    enrichWithAddresses(base).then((enriched) => {
      if (addressEnrichmentSequenceRef.current === sequenceId) setPoints(enriched);
    });
  }, [orderDetail?.checkPoints, shipmentType, minMarkers, hereApiKey, mapLanguage]);

  const handleMapPoint = (location, index) => {
    if (index == null || !location) return;
    const updated = [...points];
    updated[index] = applyCheckpointMapPoint(updated[index], location);
    setPoints(updated);
    emitChange(updated);
    onDirectoryLocationSelect?.(index, "");

    const nextIndex = updated.findIndex((p) => p.lat == null || p.lng == null);
    setActiveIndex(nextIndex === -1 ? null : nextIndex);
  };

  const updatePointFromAddressResult = (index, result, fallbackAddress) => {
    if (index == null || !result) return;
    const updated = [...points];
    const previous = updated[index] || {};
    const address = result.address || fallbackAddress || previous.address || "";
    const previousName = String(previous.name || "").trim();
    const previousAddress = String(previous.address || "").trim();
    const fallbackName = String(fallbackAddress || "").trim();
    const shouldReplaceName =
      !previousName ||
      previousName === previousAddress ||
      previousName === fallbackName;
    updated[index] = {
      ...applyCheckpointAddressResult(previous, result, fallbackAddress),
      name: shouldReplaceName ? result.name || address : previous.name,
    };
    setPoints(updated);
    emitChange(updated);
    onDirectoryLocationSelect?.(index, "");
  };

  const resolveAddress = async (index) => {
    const current = points[index];
    if (!current?.address || !hereApiKey) return;
    if (typeof current.lat === "number" && typeof current.lng === "number") {
      return;
    }
    try {
      const sequence = ++resolveSequenceRef.current;
      const result = await geocodeAddress(current.address, hereApiKey, {
        language: mapLanguage,
      });
      if (!result || sequence !== resolveSequenceRef.current) return;
      updatePointFromAddressResult(index, result, current.address);
    } catch (error) {
      console.error("Geocode failed:", error);
    }
  };

  const handleAddressChange = (index, value) => {
    resolveSequenceRef.current += 1;
    const updated = [...points];
    const previous = updated[index] || {};
    updated[index] = {
      ...previous,
      address: value,
      name: previous.name || "",
      placeId: "",
    };
    setPoints(updated);
    emitChange(updated);
    onDirectoryLocationSelect?.(index, "");
  };

  const handleAddressSearch = (index, value) => {
    const sequenceId = ++suggestSequenceRef.current;

    if (suggestTimeoutRef.current) {
      window.clearTimeout(suggestTimeoutRef.current);
      suggestTimeoutRef.current = null;
    }

    const normalizedSearch = String(value || "").trim();
    const normalizedNeedle = normalizedSearch.toLocaleLowerCase();
    const directoryMatches = uniqueDirectoryRecords(directoryLocations).filter((location) =>
      !normalizedSearch || directoryLocationSearchText(location).includes(normalizedNeedle));
    const directoryOptions = directoryMatches.map((location) => ({
      value: `directory:${location.id}`,
      label: `${location.name}${directoryLocationAddress(location) !== location.name ? ` — ${directoryLocationAddress(location)}` : ""}${!validCoordinates(location.latitude, location.longitude) ? ` — ${t("placeForm.coordinates")}` : ""}`,
      payload: { source: "DIRECTORY", location },
    }));

    setAddressOptionsByIndex((current) => ({
      ...current,
      [index]: directoryOptions.length
        ? [{ label: t("mapEditor.directoryResults"), options: directoryOptions }]
        : [],
    }));

    if (!normalizedSearch || normalizedSearch.length < 3 || !hereApiKey) {
      return;
    }

    const currentPoint = points[index];
    const nearbyPoint =
      typeof currentPoint?.lat === "number" && typeof currentPoint?.lng === "number"
        ? { lat: currentPoint.lat, lng: currentPoint.lng }
        : null;

    suggestTimeoutRef.current = window.setTimeout(async () => {
      const results = await autosuggestAddress(normalizedSearch, hereApiKey, {
        language: mapLanguage,
        limit: 6,
        at: nearbyPoint,
      });

      if (suggestSequenceRef.current !== sequenceId) return;

      const mapOptions = results.map((item) => {
          const label = item.address || item.name || normalizedSearch;
          return {
            value: label,
            label,
            payload: { source: "MAP", location: item },
          };
        });
      setAddressOptionsByIndex((current) => ({
        ...current,
        [index]: [
          ...(directoryOptions.length
            ? [{ label: t("mapEditor.directoryResults"), options: directoryOptions }]
            : []),
          ...(mapOptions.length
            ? [{ label: t("mapEditor.mapResults"), options: mapOptions }]
            : []),
        ],
      }));
    }, 220);
  };

  const handleAddressSelect = (index, value, option) => {
    resolveSequenceRef.current += 1;
    suggestSequenceRef.current += 1;
    window.clearTimeout(suggestTimeoutRef.current);
    const selectedAddress = String(value || "").trim();
    const selected = option?.payload?.location || null;
    if (selected) {
      if (option?.payload?.source === "DIRECTORY") {
        applyDirectoryLocation(index, selected);
      } else {
        updatePointFromAddressResult(index, selected, selectedAddress);
      }
    }
    setAddressOptionsByIndex((current) => ({ ...current, [index]: [] }));
  };

  const applyDirectoryLocation = (index, location) => {
    if (index == null || !location) return;
    const updated = [...points];
    const address = directoryLocationAddress(location);
    updated[index] = {
      ...(updated[index] || {}),
      lat: validCoordinates(location.latitude, location.longitude) ? Number(location.latitude) : null,
      lng: validCoordinates(location.latitude, location.longitude) ? Number(location.longitude) : null,
      address,
      name: location.name || address,
      countryCode: location.address?.countryCode || "",
      placeId: location.id,
    };
    setPoints(updated);
    emitChange(updated);
    onDirectoryLocationSelect?.(index, location.id);
  };

  const clearDirectoryLocation = (index) => {
    const updated = [...points];
    updated[index] = {
      ...(updated[index] || {}),
      lat: null,
      lng: null,
      address: "",
      name: "",
      countryCode: "",
      placeId: "",
    };
    setPoints(updated);
    emitChange(updated);
    onDirectoryLocationSelect?.(index, "");
  };

  const clearAll = () => {
    const cleared = points.map((p) => ({
      ...p,
      lat: null,
      lng: null,
      address: "",
      name: "",
      countryCode: "",
      placeId: "",
    }));
    setPoints(cleared);
    emitChange(cleared);
    setActiveIndex(0);
  };

  const getTitleForIndex = (index, total) => {
    switch (shipmentType) {
      case "CONTAINER_INBOUND":
        if (total === 3) {
          if (index === 0) return t("mapEditor.pickupFullContainer");
          if (index === 1) return t("mapEditor.deliveryGoodsLocation");
          if (index === 2) return t("mapEditor.returnEmptyContainer");
        }
        break;
      case "CONTAINER_OUTBOUND":
        if (total === 3) {
          if (index === 0) return t("mapEditor.pickupEmptyContainer");
          if (index === 1) return t("mapEditor.loadingGoodsLocation");
          if (index === 2) return t("mapEditor.returnFullContainer");
        }
        break;
      case "CONVENTIONAL_PICKUP":
      case "CONVENTIONAL_DELIVERY":
        if (total === 2) {
          return index === 0 ? t("mapEditor.pickUp") : t("mapEditor.delivery");
        }
        break;
      default:
        return `Address ${index + 1}`;
    }
    return `Address ${index + 1}`;
  };

  useEffect(() => {
    const nextIndex = points.findIndex((p) => p.lat == null || p.lng == null);
    if (nextIndex === -1) {
      setActiveIndex(null);
      return;
    }
    if (activeIndex == null || activeIndex !== nextIndex) {
      setActiveIndex(nextIndex);
    }
  }, [points, activeIndex]);

  return (
    <div className="checkpoint-map-editor-shell">
      <div className="checkpoint-map-editor">
        <div className="checkpoint-map-editor__locations">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Text type="secondary">
            {t("mapEditor.mapHint", { defaultValue: "Click the map to add points in order." })}
          </Typography.Text>
          <Button size="small" onClick={clearAll}>
            {t("mapEditor.clear", { defaultValue: "Clear" })}
          </Button>
        </div>

        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
          {points.map((p, idx) => (
            <div
              key={idx}
              style={{
                border: activeIndex === idx ? "1px solid #2f54eb" : "1px solid #e5e9f2",
                borderRadius: 10,
                padding: 10,
                background: "#fff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong>{getTitleForIndex(idx, points.length)}</strong>
                <Tag color={validCoordinates(p.lat, p.lng) ? "green" : "default"}>
                  {validCoordinates(p.lat, p.lng)
                    ? t("mapEditor.done", { defaultValue: "Set" })
                    : t("mapEditor.pending", { defaultValue: "Pending" })}
                </Tag>
              </div>

              <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
                <AutoComplete
                  status={(showErrors || touched[idx]) && !validCoordinates(p.lat, p.lng) ? "error" : undefined}
                  title={(showErrors || touched[idx]) && !validCoordinates(p.lat, p.lng) ? t("placeForm.coordinates") : undefined}
                  allowClear
                  value={p.address || ""}
                  options={addressOptionsByIndex[idx] || []}
                  onSearch={(value) => handleAddressSearch(idx, value)}
                  onSelect={(value, option) => handleAddressSelect(idx, value, option)}
                  onChange={(value, option) => {
                    if (!option?.payload) handleAddressChange(idx, value);
                  }}
                  onClear={() => clearDirectoryLocation(idx)}
                  filterOption={false}
                >
                  <Input
                    placeholder={t("mapEditor.searchDirectoryOrMap")}
                    onFocus={() => {
                      setActiveIndex(idx);
                      handleAddressSearch(idx, "");
                    }}
                    onPressEnter={() => resolveAddress(idx)}
                    onBlur={() => {
                      setTouched(prev => ({ ...prev, [idx]: true }));
                      setActiveIndex(null);
                      resolveAddress(idx);
                    }}
                  />
                </AutoComplete>
                <Input
                  value={p.name || ""}
                  status={showErrors && !p.name?.trim() ? "error" : undefined}
                  title={showErrors && !p.name?.trim() ? t("placeForm.name") : undefined}
                  placeholder={t("mapEditor.pointName", { defaultValue: "Point name" })}
                  onFocus={() => setActiveIndex(idx)}
                  onBlur={() => setActiveIndex(null)}
                  onChange={(e) => {
                    const updated = [...points];
                    updated[idx] = { ...updated[idx], name: e.target.value, placeId: "" };
                    setPoints(updated);
                    emitChange(updated);
                    onDirectoryLocationSelect?.(idx, "");
                  }}
                />
                {!p.placeId && String(p.name || p.address || "").trim() ? (
                  <Button
                    type="dashed"
                    onClick={() => onCreateDirectoryLocation?.(idx, p)}
                  >
                    {t("orderImport.createDirectoryValue")}
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        </div>

        <div className="checkpoint-map-editor__map">
          <ClickOnMap
            fitCompleteTrip
            basePoints={points}
            onBasePointUpdate={handleMapPoint}
            plannedRoute={plannedRoute}
            activeIndex={activeIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckpointMapEditor;
